import classNames from "classnames";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ChangeEvent,
  type InputHTMLAttributes
} from "react";
import { TextInputHandle } from "./textInputHandle";
import { TextInputType } from "./textInputType";

interface TextInputProps {
  id?: string;
  name?: string;
  value?: string;
  placeholder?: string;
  title?: string;
  disabled?: boolean;
  required?: boolean;
  invalid?: boolean;
  warning?: boolean;
  autocomplete?: boolean;
  inputType?: TextInputType;
  className?: string;
  containerClassName?: string;
  onValueChanged?: (value: string) => void;
  onBlur?: () => void;
  additionalAttributes?: InputHTMLAttributes<HTMLInputElement>;
}

export const TextInput = forwardRef<TextInputHandle, TextInputProps>(
  (
    {
      id,
      name,
      value = "",
      placeholder,
      title,
      disabled = false,
      required = false,
      invalid = false,
      warning = false,
      autocomplete = true,
      inputType = TextInputType.Text,
      className = "",
      containerClassName = "",
      onValueChanged,
      onBlur,
      additionalAttributes
    },
    ref
  ) => {
    const [inputValue, setInputValue] = useState<string>(value);
    const inputRef = useRef<HTMLInputElement>(null);

    // Use id as name if name is not provided
    const inputName = name || id;

    useEffect(() => {
      setInputValue(value);
    }, [value]);

    const handleInputChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        setInputValue(newValue);

        if (onValueChanged) {
          onValueChanged(newValue);
        }
      },
      [onValueChanged]
    );

    const handleBlur = useCallback(() => {
      if (onBlur) {
        onBlur();
      }
    }, [onBlur]);

    // Public methods for the imperative handle
    const focusAsync = useCallback(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, []);

    const blur = useCallback(() => {
      if (inputRef.current) {
        inputRef.current.blur();
      }
    }, []);

    const select = useCallback(() => {
      if (inputRef.current) {
        inputRef.current.select();
      }
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        focus: focusAsync,
        focusAsync: focusAsync,
        blur: blur,
        select: select
      }),
      [focusAsync, blur, select]
    );

    return (
      <div
        className={classNames(
          "rounded-tl-br-md flex h-3xl items-stretch p-xs relative border border-neutral-300",
          "transition-short-in-out hover:border-dark-blue",
          "focus-outline has-[input:focus-visible]:outline",
          {
            "input--error !border-error-200 has-[input:focus-visible]:outline-error-200/75":
              invalid,
            "input--warning !border-warning-200 has-[textarea:focus-visible]:outline-warning-200/75":
              warning
          },
          containerClassName
        )}
      >
        <input
          ref={inputRef}
          id={id}
          name={inputName}
          type={inputType}
          value={inputValue}
          placeholder={placeholder}
          title={title}
          disabled={disabled}
          required={required}
          autoComplete={autocomplete ? "on" : "off"}
          className={classNames(
            "w-full py-sm px-md border-0 text-dark-blue text-t-sm",
            "focus-visible:outline-none placeholder:text-t-neutral-700 rounded-tl-br-sm", // rounded on input as well as container so if user autofills browser applied blue background looks good
            {
              "bg-neutral-100/30 border-neutral-400/30 cursor-not-allowed": disabled,
              "placeholder:text-neutral-400/70 text-neutral-600/70": disabled,
              "border-error-200 focus:border-error-200 focus:ring-error-200": invalid,
              "border-warning-200 focus:border-warning-200 focus:ring-warning-200":
                warning
            },
            className
          )}
          onChange={handleInputChange}
          onBlur={handleBlur}
          data-f-datainput="true"
          {...additionalAttributes}
        />
      </div>
    );
  }
);

TextInput.displayName = "TextInput";

export default TextInput;
